/**
 * Utilidades de tablero clínico para vista previa de entregables.
 * Alineadas con PatientProfile / ClinicalSummary (mismos umbrales).
 */

const safeObj = (o) => (o && typeof o === "object" ? o : {});

function normKey(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, " ");
}

function interpretZaritBurdenText(score) {
  const n = Number(score);
  if (Number.isNaN(n)) return "";
  if (n <= 10) return "Sin sobrecarga";
  if (n <= 20) return "Sobrecarga leve";
  return "Sobrecarga intensa";
}

function interpretTugDashboardText(sec) {
  const s = Number(sec);
  if (Number.isNaN(s)) return "";
  if (s < 10) return "Movilidad dentro de rango esperado";
  if (s < 20) return "Funcionalidad conservada";
  if (s < 30) return "Movilidad reducida; riesgo aumentado si ≥20 s";
  return "Alteración marcada; alto riesgo funcional";
}

export function normalizeConsultationScales(escalasRaw) {
  const out = {};
  for (const [rawKey, val] of Object.entries(safeObj(escalasRaw))) {
    const nk = normKey(rawKey);
    const p = val && typeof val === "object" ? (val.puntaje ?? val.value ?? val.score) : val;
    if (p == null && p !== 0) continue;
    const wrapped =
      val && typeof val === "object"
        ? { puntaje: p, value: p, interpretacion: val.interpretacion }
        : { puntaje: p, value: p };
    if (nk.includes("barthel")) out.barthel = wrapped;
    else if (nk.includes("katz")) out.katz = wrapped;
    else if (nk.includes("sppb")) out.sppb = wrapped;
    else if (nk.includes("mini-cog") || nk.includes("mini cog") || nk.includes("minicog")) out.mini_cog = wrapped;
    else if (nk === "cam" || nk.includes("confusion assessment")) out.cam = wrapped;
    else if (nk.includes("moca")) out.moca = wrapped;
    else if (nk.includes("mmse") || nk.includes("minimental")) out.mmse = wrapped;
    else if (nk.includes("pfeiffer")) out.pfeiffer = wrapped;
    else if (nk.includes("mna")) out.mna_sf = wrapped;
    else if (nk.includes("morse")) out.morse = wrapped;
    else if (nk.includes("tinetti")) out.tinetti = wrapped;
    else if (nk.includes("yesavage") || nk === "gds") out.yesavage = wrapped;
    else if (nk.includes("gad7") || nk.includes("gad-7") || nk.includes("gad 7")) out.gad7 = wrapped;
    else if (nk.includes("phq9") || nk.includes("phq-9") || nk.includes("phq 9")) out.phq9 = wrapped;
    else if (nk.includes("frail")) out.frail = wrapped;
    else if (nk.includes("sarc-f") || nk.includes("sarc f") || nk.includes("sarcf")) out.sarc_f = wrapped;
    else if (nk.includes("cfs")) out.cfs = wrapped;
    else if (nk.includes("zarit")) out.zarit = wrapped;
    else if (nk === "tug" || nk.includes("timed up") || nk.includes("t u g")) out.tug = wrapped;
    else if (nk.includes("polifarmacia") && nk.includes("simple")) out.polifarmacia_simple = wrapped;
    else if (nk.includes("norton")) out.norton = wrapped;
    else if (nk.includes("lawton")) out.lawton = wrapped;
    else if (nk.includes("charlson")) out.charlson = wrapped;
    else if (nk.includes("eva") || nk.includes("dolor") || nk.includes("pain")) out.eva_dolor = wrapped;
    else if (nk.includes("mmse") || nk.includes("folstein")) out.mmse = wrapped;
    else if (nk.includes("clinical frailty") || nk.includes("cfs")) out.cfs = wrapped;
  }
  return out;
}

const RISK_TW = {
  bajo: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    bar: "bg-emerald-500",
  },
  medio: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    bar: "bg-amber-500",
  },
  alto: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    bar: "bg-red-500",
  },
  sin_datos: {
    bg: "bg-slate-50",
    border: "border-slate-200",
    text: "text-slate-400",
    bar: "bg-slate-300",
  },
};

export function getDashboardClinicalStatusDomains(displayScales) {
  const s = displayScales || {};
  const domains = [
    {
      label: "Funcional",
      getValue: () => {
        const b = s.barthel?.puntaje ?? s.barthel?.value;
        const k = s.katz?.puntaje ?? s.katz?.value;
        const sp = s.sppb?.puntaje ?? s.sppb?.value;
        if (b !== undefined)
          return {
            score: b,
            max: 100,
            label: b >= 90 ? "Independiente" : b >= 60 ? "Dep. leve" : b >= 40 ? "Dep. moderada" : "Dep. severa",
            risk: b >= 90 ? "bajo" : b >= 60 ? "medio" : "alto",
            sub: `Barthel ${b}/100`,
          };
        if (k !== undefined)
          return {
            score: k,
            max: 6,
            label: k === 6 ? "Independiente" : k >= 4 ? "Dep. leve" : "Dep. severa",
            risk: k === 6 ? "bajo" : k >= 4 ? "medio" : "alto",
            sub: `Katz ${k}/6`,
          };
        if (sp !== undefined)
          return {
            score: sp,
            max: 12,
            label: sp <= 3 ? "Discapacidad funcional severa" : sp <= 6 ? "Limitación moderada" : sp <= 9 ? "Limitación leve" : "Funcionalidad conservada",
            risk: sp <= 3 ? "alto" : sp <= 6 ? "medio" : sp <= 9 ? "medio" : "bajo",
            sub: `SPPB ${sp}/12`,
          };
        return null;
      },
    },
    {
      label: "Movilidad",
      getValue: () => {
        const t = s.tug?.puntaje ?? s.tug?.value;
        if (t === undefined) return null;
        const sec = Number(t);
        if (Number.isNaN(sec)) return null;
        const labelText =
          (s.tug?.interpretacion && String(s.tug.interpretacion).trim()) || interpretTugDashboardText(sec);
        return {
          score: Math.max(0, 60 - Math.min(sec, 60)),
          max: 60,
          label: labelText,
          risk: sec < 20 ? "bajo" : sec < 30 ? "medio" : "alto",
          sub: `TUG ${sec} s`,
        };
      },
    },
    {
      label: "Cognitivo",
      getValue: () => {
        const m = s.mmse?.puntaje ?? s.mmse?.value;
        const mc = s.moca?.puntaje ?? s.moca?.value;
        const miniCog = s.mini_cog?.puntaje ?? s.mini_cog?.value;
        const cam = s.cam?.puntaje ?? s.cam?.value;
        const p = s.pfeiffer?.puntaje ?? s.pfeiffer?.value;
        if (miniCog !== undefined)
          return {
            score: miniCog ? 0 : 1,
            max: 1,
            label: miniCog ? "Tamizaje positivo para deterioro cognitivo" : "Tamizaje negativo",
            risk: miniCog ? "alto" : "bajo",
            sub: "Mini-Cog",
          };
        if (cam !== undefined)
          return {
            score: cam ? 0 : 1,
            max: 1,
            label: cam ? "Compatible con delirium" : "No cumple criterios de delirium",
            risk: cam ? "alto" : "bajo",
            sub: "CAM",
          };
        if (mc !== undefined)
          return {
            score: mc,
            max: 30,
            label: mc >= 26 ? "Cognición en rango esperado" : mc >= 18 ? "Sospecha de deterioro leve" : mc >= 10 ? "Deterioro moderado" : "Deterioro severo",
            risk: mc >= 26 ? "bajo" : mc >= 18 ? "medio" : "alto",
            sub: `MoCA ${mc}/30`,
          };
        if (m !== undefined)
          return {
            score: m,
            max: 30,
            label: m >= 24 ? "Normal" : m >= 18 ? "Deterioro leve" : m >= 10 ? "Moderado" : "Severo",
            risk: m >= 24 ? "bajo" : m >= 18 ? "medio" : "alto",
            sub: `MMSE ${m}/30`,
          };
        if (p !== undefined)
          return {
            score: 10 - p,
            max: 10,
            label: p <= 2 ? "Normal" : p <= 4 ? "Deterioro leve" : p <= 7 ? "Moderado" : "Severo",
            risk: p <= 2 ? "bajo" : p <= 4 ? "medio" : "alto",
            sub: `Pfeiffer ${p} errores`,
          };
        return null;
      },
    },
    {
      label: "Fragilidad",
      getValue: () => {
        const f = s.frail?.puntaje ?? s.frail?.value;
        const sf = s.sarc_f?.puntaje ?? s.sarc_f?.value;
        const c = s.cfs?.puntaje ?? s.cfs?.value;
        if (f !== undefined)
          return {
            score: 5 - f,
            max: 5,
            label: f === 0 ? "Robusto" : f <= 2 ? "Pre-frágil" : "Frágil",
            risk: f === 0 ? "bajo" : f <= 2 ? "medio" : "alto",
            sub: `FRAIL ${f}/5`,
          };
        if (sf !== undefined)
          return {
            score: 10 - sf,
            max: 10,
            label:
              sf <= 3 ? "Bajo riesgo (tamizaje)" : sf <= 6 ? "Riesgo intermedio" : "Alto riesgo de sarcopenia",
            risk: sf <= 3 ? "bajo" : sf <= 6 ? "medio" : "alto",
            sub: `SARC-F ${sf}/10`,
          };
        if (c !== undefined)
          return {
            score: 9 - c,
            max: 9,
            label: c <= 3 ? "Buena forma" : c <= 5 ? "Vulnerable" : "Frágil",
            risk: c <= 3 ? "bajo" : c <= 5 ? "medio" : "alto",
            sub: `CFS ${c}/9`,
          };
        return null;
      },
    },
    {
      label: "Riesgo Caídas",
      getValue: () => {
        const mo = s.morse?.puntaje ?? s.morse?.value;
        const ti = s.tinetti?.puntaje ?? s.tinetti?.value;
        const tug = s.tug?.puntaje ?? s.tug?.value;
        if (mo !== undefined)
          return {
            score: 125 - mo,
            max: 125,
            label: mo <= 24 ? "Bajo" : mo <= 44 ? "Moderado" : "Alto",
            risk: mo <= 24 ? "bajo" : mo <= 44 ? "medio" : "alto",
            sub: `Morse ${mo}`,
          };
        if (ti !== undefined)
          return {
            score: ti,
            max: 28,
            label: ti >= 24 ? "Bajo" : ti >= 19 ? "Moderado" : "Alto",
            risk: ti >= 24 ? "bajo" : ti >= 19 ? "medio" : "alto",
            sub: `Tinetti ${ti}/28`,
          };
        if (tug !== undefined) {
          const sec = Number(tug);
          const risk = sec < 20 ? "bajo" : sec < 30 ? "medio" : "alto";
          const label =
            sec < 10 ? "Dentro de rango" : sec < 20 ? "Conservada" : sec < 30 ? "Riesgo aumentado (≥20 s)" : "Alteración marcada";
          return {
            score: Math.max(0, 60 - sec),
            max: 60,
            label,
            risk,
            sub: `TUG ${sec} s`,
          };
        }
        return null;
      },
    },
    {
      label: "Nutrición",
      getValue: () => {
        const n = s.mna_sf?.puntaje ?? s.mna_sf?.value;
        if (n !== undefined)
          return {
            score: n,
            max: 14,
            label: n >= 12 ? "Normal" : n >= 8 ? "Riesgo" : "Malnutrición",
            risk: n >= 12 ? "bajo" : n >= 8 ? "medio" : "alto",
            sub: `MNA ${n}/14`,
          };
        return null;
      },
    },
    {
      label: "Ánimo",
      getValue: () => {
        const y = s.yesavage?.puntaje ?? s.yesavage?.value;
        const g7 = s.gad7?.puntaje ?? s.gad7?.value;
        const p9 = s.phq9?.puntaje ?? s.phq9?.value;
        if (g7 !== undefined)
          return {
            score: 21 - g7,
            max: 21,
            label:
              g7 <= 4
                ? "Ansiedad mínima (tamizaje)"
                : g7 <= 9
                  ? "Ansiedad leve"
                  : g7 <= 14
                    ? "Ansiedad moderada"
                    : "Ansiedad grave",
            risk: g7 <= 4 ? "bajo" : g7 <= 14 ? "medio" : "alto",
            sub: `GAD-7 ${g7}/21`,
          };
        if (p9 !== undefined)
          return {
            score: 27 - p9,
            max: 27,
            label: p9 <= 4 ? "Depresión mínima" : p9 <= 9 ? "Depresión leve" : p9 <= 14 ? "Depresión moderada" : p9 <= 19 ? "Depresión moderadamente grave" : "Depresión grave",
            risk: p9 <= 4 ? "bajo" : p9 <= 14 ? "medio" : "alto",
            sub: `PHQ-9 ${p9}/27`,
          };
        if (y !== undefined)
          return {
            score: 15 - y,
            max: 15,
            label: y <= 4 ? "Normal" : y <= 9 ? "Depresión leve" : "Depresión severa",
            risk: y <= 4 ? "bajo" : y <= 9 ? "medio" : "alto",
            sub: `Yesavage ${y}/15`,
          };
        return null;
      },
    },
    {
      label: "Farmacoterapia",
      getValue: () => {
        const poly = s.polifarmacia_simple?.puntaje ?? s.polifarmacia_simple?.value;
        if (poly === undefined) return null;
        const n = Number(poly);
        if (Number.isNaN(n)) return null;
        const labelText =
          (s.polifarmacia_simple?.interpretacion && String(s.polifarmacia_simple.interpretacion).trim()) ||
          (n <= 4 ? "Bajo riesgo" : n <= 9 ? "Polifarmacia" : "Polifarmacia severa");
        return {
          score: Math.max(0, 20 - Math.min(n, 20)),
          max: 20,
          label: labelText,
          risk: n <= 4 ? "bajo" : n <= 9 ? "medio" : "alto",
          sub: `${n} medicamentos`,
        };
      },
    },
    {
      label: "Cuidador",
      getValue: () => {
        const z = s.zarit?.puntaje ?? s.zarit?.value;
        if (z === undefined) return null;
        const zn = Number(z);
        if (Number.isNaN(zn)) return null;
        const labelText =
          (s.zarit?.interpretacion && String(s.zarit.interpretacion).trim()) || interpretZaritBurdenText(zn);
        return {
          score: Math.max(0, 48 - zn),
          max: 48,
          label: labelText,
          risk: zn <= 10 ? "bajo" : zn <= 20 ? "medio" : "alto",
          sub: `Zarit ${zn}/48`,
        };
      },
    },
  ];

  return domains.map((d) => {
    const val = d.getValue();
    const risk = val ? val.risk : "sin_datos";
    const pct = val ? Math.round((val.score / val.max) * 100) : 0;
    const tw = RISK_TW[risk] || RISK_TW.sin_datos;
    return { label: d.label, val, risk, pct, tw };
  });
}

const PROFILE_STATUS_TW = {
  stable: {
    tile: "bg-emerald-50 border-emerald-200",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  risk: {
    tile: "bg-amber-50 border-amber-200",
    text: "text-amber-700",
    dot: "bg-amber-500",
  },
  "high-risk": {
    tile: "bg-red-50 border-red-200",
    text: "text-red-700",
    dot: "bg-red-500",
  },
  unknown: {
    tile: "bg-slate-50 border-slate-200",
    text: "text-slate-500",
    dot: "bg-slate-300",
  },
};

export function getGeriatricProfileVisualTiles(displayScales) {
  const scales = displayScales || {};
  const categories = [
    {
      label: "Funcional",
      getStatus: () => {
        const barthel = scales.barthel?.puntaje ?? scales.barthel?.value;
        const sppb = scales.sppb?.puntaje ?? scales.sppb?.value;
        if (barthel !== undefined) {
          if (barthel >= 90) return { status: "stable", label: "Estable", scales: `Barthel: ${barthel}` };
          if (barthel >= 60) return { status: "risk", label: "Riesgo", scales: `Barthel: ${barthel}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `Barthel: ${barthel}` };
        }
        if (sppb !== undefined) {
          if (sppb >= 10) return { status: "stable", label: "Estable", scales: `SPPB: ${sppb}` };
          if (sppb >= 7) return { status: "risk", label: "Riesgo", scales: `SPPB: ${sppb}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `SPPB: ${sppb}` };
        }
        return { status: "unknown", label: "Sin datos", scales: "" };
      },
    },
    {
      label: "Cognitivo",
      getStatus: () => {
        const mmse = scales.mmse?.puntaje ?? scales.mmse?.value;
        const moca = scales.moca?.puntaje ?? scales.moca?.value;
        const miniCog = scales.mini_cog?.puntaje ?? scales.mini_cog?.value;
        const cam = scales.cam?.puntaje ?? scales.cam?.value;
        const pfeiffer = scales.pfeiffer?.puntaje ?? scales.pfeiffer?.value;
        if (miniCog !== undefined) {
          if (miniCog) return { status: "high-risk", label: "Alto Riesgo", scales: "Mini-Cog positivo" };
          return { status: "stable", label: "Estable", scales: "Mini-Cog negativo" };
        }
        if (cam !== undefined) {
          if (cam) return { status: "high-risk", label: "Delirium", scales: "CAM: compatible con delirium" };
          return { status: "stable", label: "Estable", scales: "CAM: sin criterios" };
        }
        if (moca !== undefined) {
          if (moca >= 26) return { status: "stable", label: "Estable", scales: `MoCA: ${moca}` };
          if (moca >= 18) return { status: "risk", label: "Riesgo", scales: `MoCA: ${moca}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `MoCA: ${moca}` };
        }
        if (mmse !== undefined) {
          if (mmse >= 24) return { status: "stable", label: "Estable", scales: `MMSE: ${mmse}` };
          if (mmse >= 18) return { status: "risk", label: "Riesgo", scales: `MMSE: ${mmse}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `MMSE: ${mmse}` };
        }
        if (pfeiffer !== undefined) {
          if (pfeiffer <= 2) return { status: "stable", label: "Estable", scales: `Pfeiffer: ${pfeiffer}` };
          if (pfeiffer <= 4) return { status: "risk", label: "Riesgo", scales: `Pfeiffer: ${pfeiffer}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `Pfeiffer: ${pfeiffer}` };
        }
        return { status: "unknown", label: "Sin datos", scales: "" };
      },
    },
    {
      label: "Nutricional",
      getStatus: () => {
        const mna = scales.mna_sf?.puntaje ?? scales.mna_sf?.value;
        if (mna === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        if (mna >= 12) return { status: "stable", label: "Estable", scales: `MNA: ${mna}` };
        if (mna >= 8) return { status: "risk", label: "Riesgo", scales: `MNA: ${mna}` };
        return { status: "high-risk", label: "Alto Riesgo", scales: `MNA: ${mna}` };
      },
    },
    {
      label: "Caídas",
      getStatus: () => {
        const morse = scales.morse?.puntaje ?? scales.morse?.value;
        const tinetti = scales.tinetti?.puntaje ?? scales.tinetti?.value;
        if (morse !== undefined) {
          if (morse <= 24) return { status: "stable", label: "Bajo Riesgo", scales: `Morse: ${morse}` };
          if (morse <= 44) return { status: "risk", label: "Riesgo Medio", scales: `Morse: ${morse}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `Morse: ${morse}` };
        }
        if (tinetti !== undefined) {
          if (tinetti >= 24) return { status: "stable", label: "Bajo Riesgo", scales: `Tinetti: ${tinetti}` };
          if (tinetti >= 19) return { status: "risk", label: "Riesgo Medio", scales: `Tinetti: ${tinetti}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `Tinetti: ${tinetti}` };
        }
        return { status: "unknown", label: "Sin datos", scales: "" };
      },
    },
    {
      label: "Fragilidad",
      getStatus: () => {
        const frail = scales.frail?.puntaje ?? scales.frail?.value;
        const sarcF = scales.sarc_f?.puntaje ?? scales.sarc_f?.value;
        const cfs = scales.cfs?.puntaje ?? scales.cfs?.value;
        if (frail !== undefined) {
          if (frail === 0) return { status: "stable", label: "Robusto", scales: `FRAIL: ${frail}` };
          if (frail <= 2) return { status: "risk", label: "Pre-frágil", scales: `FRAIL: ${frail}` };
          return { status: "high-risk", label: "Frágil", scales: `FRAIL: ${frail}` };
        }
        if (sarcF !== undefined) {
          if (sarcF <= 3) return { status: "stable", label: "Bajo riesgo", scales: `SARC-F: ${sarcF}` };
          if (sarcF <= 6) return { status: "risk", label: "Riesgo intermedio", scales: `SARC-F: ${sarcF}` };
          return { status: "high-risk", label: "Alto riesgo", scales: `SARC-F: ${sarcF}` };
        }
        if (cfs !== undefined) {
          if (cfs <= 3) return { status: "stable", label: "Estable", scales: `CFS: ${cfs}` };
          if (cfs <= 5) return { status: "risk", label: "Riesgo", scales: `CFS: ${cfs}` };
          return { status: "high-risk", label: "Alto Riesgo", scales: `CFS: ${cfs}` };
        }
        return { status: "unknown", label: "Sin datos", scales: "" };
      },
    },
    {
      label: "Emocional",
      getStatus: () => {
        const yesavage = scales.yesavage?.puntaje ?? scales.yesavage?.value;
        const gad7 = scales.gad7?.puntaje ?? scales.gad7?.value;
        const phq9 = scales.phq9?.puntaje ?? scales.phq9?.value;
        if (gad7 !== undefined) {
          if (gad7 <= 4) return { status: "stable", label: "Normal", scales: `GAD-7: ${gad7}` };
          if (gad7 <= 14) return { status: "risk", label: "Ansiedad leve-moderada", scales: `GAD-7: ${gad7}` };
          return { status: "high-risk", label: "Ansiedad", scales: `GAD-7: ${gad7}` };
        }
        if (phq9 !== undefined) {
          if (phq9 <= 4) return { status: "stable", label: "Normal", scales: `PHQ-9: ${phq9}` };
          if (phq9 <= 14) return { status: "risk", label: "Depresión leve-moderada", scales: `PHQ-9: ${phq9}` };
          return { status: "high-risk", label: "Depresión", scales: `PHQ-9: ${phq9}` };
        }
        if (yesavage === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        if (yesavage <= 4) return { status: "stable", label: "Normal", scales: `Yesavage: ${yesavage}` };
        if (yesavage <= 8) return { status: "risk", label: "Depresión leve", scales: `Yesavage: ${yesavage}` };
        return { status: "high-risk", label: "Depresión", scales: `Yesavage: ${yesavage}` };
      },
    },
    {
      label: "Movilidad",
      getStatus: () => {
        const tug = scales.tug?.puntaje ?? scales.tug?.value;
        if (tug === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        const sec = Number(tug);
        if (Number.isNaN(sec)) return { status: "unknown", label: "Sin datos", scales: "" };
        const interp =
          (scales.tug?.interpretacion && String(scales.tug.interpretacion).trim()) ||
          interpretTugDashboardText(sec);
        if (sec < 10) return { status: "stable", label: interp, scales: `TUG ${sec}s` };
        if (sec < 20) return { status: "stable", label: interp, scales: `TUG ${sec}s` };
        if (sec < 30) return { status: "risk", label: interp, scales: `TUG ${sec}s` };
        return { status: "high-risk", label: interp, scales: `TUG ${sec}s` };
      },
    },
    {
      label: "Farmacoterapia",
      getStatus: () => {
        const poly = scales.polifarmacia_simple?.puntaje ?? scales.polifarmacia_simple?.value;
        if (poly === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        const n = Number(poly);
        if (Number.isNaN(n)) return { status: "unknown", label: "Sin datos", scales: "" };
        const interp =
          (scales.polifarmacia_simple?.interpretacion && String(scales.polifarmacia_simple.interpretacion).trim()) ||
          (n <= 4 ? "Bajo riesgo" : n <= 9 ? "Polifarmacia" : "Polifarmacia severa");
        if (n <= 4) return { status: "stable", label: interp, scales: `${n} medicamentos` };
        if (n <= 9) return { status: "risk", label: interp, scales: `${n} medicamentos` };
        return { status: "high-risk", label: interp, scales: `${n} medicamentos` };
      },
    },
    {
      label: "Cuidador",
      getStatus: () => {
        const z = scales.zarit?.puntaje ?? scales.zarit?.value;
        if (z === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        const zn = Number(z);
        if (Number.isNaN(zn)) return { status: "unknown", label: "Sin datos", scales: "" };
        const interp =
          (scales.zarit?.interpretacion && String(scales.zarit.interpretacion).trim()) ||
          interpretZaritBurdenText(zn);
        if (zn <= 10) return { status: "stable", label: interp, scales: `Zarit: ${zn}` };
        if (zn <= 20) return { status: "risk", label: interp, scales: `Zarit: ${zn}` };
        return { status: "high-risk", label: interp, scales: `Zarit: ${zn}` };
      },
    },
    {
      label: "Úlceras/Presión",
      getStatus: () => {
        const norton = scales.norton?.puntaje ?? scales.norton?.value;
        if (norton === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        const n = Number(norton);
        if (Number.isNaN(n)) return { status: "unknown", label: "Sin datos", scales: "" };
        if (n >= 18) return { status: "stable", label: "Sin riesgo", scales: `Norton: ${n}` };
        if (n >= 14) return { status: "risk", label: "Riesgo leve", scales: `Norton: ${n}` };
        if (n >= 10) return { status: "risk", label: "Riesgo moderado", scales: `Norton: ${n}` };
        return { status: "high-risk", label: "Alto riesgo", scales: `Norton: ${n}` };
      },
    },
    {
      label: "Instrumental",
      getStatus: () => {
        const lawton = scales.lawton?.puntaje ?? scales.lawton?.value;
        if (lawton === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        const n = Number(lawton);
        if (Number.isNaN(n)) return { status: "unknown", label: "Sin datos", scales: "" };
        if (n >= 7) return { status: "stable", label: "Independiente", scales: `Lawton: ${n}` };
        if (n >= 4) return { status: "risk", label: "Dep. parcial", scales: `Lawton: ${n}` };
        return { status: "high-risk", label: "Dep. severa", scales: `Lawton: ${n}` };
      },
    },
    {
      label: "Comorbilidad",
      getStatus: () => {
        const charlson = scales.charlson?.puntaje ?? scales.charlson?.value;
        if (charlson === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        const n = Number(charlson);
        if (Number.isNaN(n)) return { status: "unknown", label: "Sin datos", scales: "" };
        if (n === 0) return { status: "stable", label: "Sin comorbilidad", scales: `Charlson: ${n}` };
        if (n <= 2) return { status: "risk", label: "Comorbilidad leve", scales: `Charlson: ${n}` };
        if (n <= 4) return { status: "risk", label: "Comorbilidad moderada", scales: `Charlson: ${n}` };
        return { status: "high-risk", label: "Comorbilidad severa", scales: `Charlson: ${n}` };
      },
    },
    {
      label: "Dolor",
      getStatus: () => {
        const eva = scales.eva_dolor?.puntaje ?? scales.eva_dolor?.value;
        if (eva === undefined) return { status: "unknown", label: "Sin datos", scales: "" };
        const n = Number(eva);
        if (Number.isNaN(n)) return { status: "unknown", label: "Sin datos", scales: "" };
        if (n === 0) return { status: "stable", label: "Sin dolor", scales: `EVA: ${n}` };
        if (n <= 3) return { status: "stable", label: "Dolor leve", scales: `EVA: ${n}` };
        if (n <= 6) return { status: "risk", label: "Dolor moderado", scales: `EVA: ${n}` };
        return { status: "high-risk", label: "Dolor severo", scales: `EVA: ${n}` };
      },
    },
  ];

  return categories.map((cat) => {
    const result = cat.getStatus();
    const tw = PROFILE_STATUS_TW[result.status] || PROFILE_STATUS_TW.unknown;
    return { label: cat.label, result, tw };
  });
}
